/**
 * 会话编排：唯一以 `/v1/auth/me` 为准的登录态判定。
 *
 * - `ensureSession()`：进入受保护路由时调用。已认证直接返回；进行中的请求去重；
 *   401 → `unauthenticated`（apiClient 同时会触发跳登录）；网络/5xx → `error`
 *   （**不**当作未登录，避免一次抖动把用户踢到登录页）。
 * - `signIn()` / `signOut()`：登录/退出后直接更新内存会话。
 *
 * 本模块是 apiClient 与 authStore 之间的粘合层，避免二者互相 import 形成环。
 */

import { ApiClientError, apiChangeOwnPassword, apiFetchMe, apiLogin, apiLogout } from "./apiClient.js";
import { useAuthStore } from "../store/authStore.js";

let inflight: Promise<void> | null = null;

function messageOf(error: unknown): string {
  if (error instanceof ApiClientError) {
    if (error.code === "AUTH_UNAVAILABLE") {
      return "服务端认证未就绪（AUTH_UNAVAILABLE）：API 需要配置 DATABASE_URL 并执行迁移后重启；这不是「未登录」。";
    }
    return error.httpStatus === 0 ? `无法连接服务端：${error.message}` : `${error.code}：${error.message}`;
  }
  return error instanceof Error ? error.message : String(error);
}

/** 以 `/v1/auth/me` 为准校验会话（幂等、带去重）。 */
export function ensureSession(): Promise<void> {
  const store = useAuthStore.getState();
  if (store.status === "authenticated") return Promise.resolve();
  if (inflight !== null) return inflight;

  store.setChecking();
  inflight = apiFetchMe()
    .then((user) => {
      useAuthStore.getState().setAuthenticated(user);
    })
    .catch((error: unknown) => {
      const current = useAuthStore.getState();
      if (error instanceof ApiClientError && error.isUnauthenticated) {
        // apiClient 已清会话并（在有处理器时）跳登录；这里只兜底状态。
        if (current.status !== "unauthenticated") current.markUnauthenticated();
        return;
      }
      current.setError(messageOf(error));
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/** 重新校验（错误态的重试按钮）。 */
export function retrySession(): Promise<void> {
  useAuthStore.getState().reset();
  return ensureSession();
}

/** 登录失败会抛 `ApiClientError`（`INVALID_CREDENTIALS`），由登录页展示。 */
export async function signIn(username: string, password: string): Promise<void> {
  // ① 登录**只用来判定成功**（401/凭据错误会在这里抛出 ⇒ 不会走到下面）。
  // ⛔ **不要**把登录响应的 user 存进 store：它**不带 `featureAccess`**（只有 `/auth/me` 带），
  //    而新口径下 `featureAccess` 缺失 ⇒ **一切判定 fail closed** ⇒ 整个界面（含「权限管理」）不可用。
  //    这正是 2026-10-08 线上"权限管理打不开"的根因（登录后立刻打不开，刷新一次就恢复）。
  await apiLogin(username, password);
  // ② ⭐ **唯一数据来源**：`/auth/me`（含 `featureAccess`）。取不到 ⇒ **明确失败**（不放宽 fail closed）。
  let user: Awaited<ReturnType<typeof apiFetchMe>>;
  try {
    user = await apiFetchMe();
  } catch (cause) {
    // ⚠️ "取不到权限信息" ≠ "没有权限"：这里**明确报错**并让登录流程失败（不静默放行、也不伪装成登录成功）。
    void cause;
    throw new ApiClientError({
      httpStatus: 0,
      code: "PERMISSIONS_UNAVAILABLE",
      message: "登录成功，但权限信息未能取到（/auth/me 失败）。请刷新页面重试。",
      details: [],
    });
  }
  useAuthStore.getState().setAuthenticated(user);
}

/** 退出：即使服务端 logout 失败，也要清掉本地内存会话。 */
/**
 * ⭐ 自助改密：成功后**必须重取 `/auth/me`**（服务端刚清除 `must_change_password` ⇒ 标记要跟着更新，
 * 否则前端会继续按"必须改密"渲染、用户以为没生效）。
 * ⛔ 失败时**把服务端错误原样抛出**（含字段级 detail，如"当前密码不正确"）供 UI 逐条展示。
 */
export async function changeOwnPassword(input: {
  readonly currentPassword: string;
  readonly newPassword: string;
}): Promise<void> {
  await apiChangeOwnPassword(input);
  const user = await apiFetchMe();
  useAuthStore.getState().setAuthenticated(user);
}

export async function signOut(): Promise<void> {
  try {
    await apiLogout();
  } finally {
    useAuthStore.getState().markUnauthenticated();
  }
}
